import { useEffect, useState, useCallback } from 'react';
import { getSDK } from '@/store/sdkStore';
import type { Report } from '@zdc/sdk';

const sdk = () => getSDK();

export type { Report };
export type ReportFormat = 1 | 2 | 3 | 4 | 5;
export const ReportFormat = {
  JSON: 1 as ReportFormat,
  HTML: 2 as ReportFormat,
  CSV: 3 as ReportFormat,
  XLSX: 4 as ReportFormat,
  PDF: 5 as ReportFormat,
};

export function useReports(folderId?: string) {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sdk().report.listReports({ folderId: folderId ?? '' });
      setReports((res.items || []) as Report[]);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { reports, loading, error, refresh };
}

export function useReport(id: string | undefined) {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await sdk().report.getReport({ id }));
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { report, loading, error, refresh };
}

export async function createReport(req: { name: string; description: string; definitionJson: string; isTemplate?: boolean; folderId?: string }): Promise<Report> {
  return sdk().report.createReport(req as any) as Promise<Report>;
}

export async function updateReport(req: { id: string; name: string; description: string; definitionJson: string; isTemplate?: boolean; folderId?: string }): Promise<Report> {
  return sdk().report.updateReport(req as any) as Promise<Report>;
}

export async function deleteReport(id: string): Promise<void> {
  await sdk().report.deleteReport({ id } as any);
}

export async function renderReport(req: { id: string; parametersJson?: string; format?: ReportFormat }): Promise<unknown> {
  const res = await sdk().report.renderReport({
    id: req.id,
    parametersJson: req.parametersJson || '',
    format: req.format || ReportFormat.JSON,
  } as any);
  if (!res.ok) {
    throw new Error(res.errorMessage || 'Render failed');
  }
  return JSON.parse(res.dataJson || '{}');
}

export async function exportReport(req: { id: string; parametersJson?: string; format: ReportFormat }): Promise<void> {
  const res = await sdk().report.exportReport({
    id: req.id,
    parametersJson: req.parametersJson || '',
    format: req.format,
  } as any);
  if (!res.data || res.data.length === 0) {
    throw new Error(res.errorMessage || 'Export failed');
  }
  const blob = new Blob([res.data as unknown as BlobPart], { type: res.contentType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = res.filename || 'report';
  a.click();
  URL.revokeObjectURL(url);
}
